import {Kind, print} from 'graphql';
import {
  attachHiddenDocumentMetadata,
  filterInlineFragmentRefsBySelections,
  getFragmentOwnerDocs,
  getInlineFragmentRefs,
} from 'src/internal/shared/utils/hiddenDocumentMetadata';

import type {
  DocumentNode,
  FragmentDefinitionNode,
  OperationDefinitionNode,
  SelectionNode,
  VariableDefinitionNode,
} from 'graphql';
import type {HiddenDocumentMetadata as SharedHiddenDocumentMetadata} from 'src/internal/shared/utils/hiddenDocumentMetadata';

type CreateQueryDocumentMetadata = SharedHiddenDocumentMetadata & {
  source?: DocumentNode;
};

function isDocumentNode(value: unknown): value is DocumentNode {
  return (
    value !== null &&
    typeof value === 'object' &&
    'kind' in value &&
    'definitions' in value
  );
}

export function getOperationName(operation: OperationDefinitionNode): string {
  if (!operation.name) {
    throw new Error(
      `Cannot get an operation name from an anonymous operation.`,
    );
  }

  return operation.name.value;
}

export function getQueryName(document: DocumentNode): string {
  const queryDefinition = document.definitions.find(
    (def): def is OperationDefinitionNode =>
      def.kind === Kind.OPERATION_DEFINITION && def.operation === 'query',
  );

  if (!queryDefinition) {
    throw new Error(
      `There is no query defined on this document: ${print(document)}`,
    );
  }

  return getOperationName(queryDefinition);
}

function createQueryOperationNode(
  name: string,
  topLevelVariableDefinitions: ReadonlyArray<VariableDefinitionNode>,
  topLevelFields: ReadonlyArray<SelectionNode>,
): OperationDefinitionNode {
  return {
    kind: Kind.OPERATION_DEFINITION,
    operation: 'query',
    name: {
      kind: Kind.NAME,
      value: name,
    },
    variableDefinitions: topLevelVariableDefinitions,
    directives: [],
    selectionSet: {
      kind: Kind.SELECTION_SET,
      selections: topLevelFields,
    },
  };
}

function getResolvedHiddenDocumentMetadata(
  queryFields: ReadonlyArray<SelectionNode>,
  metadata?: DocumentNode | CreateQueryDocumentMetadata,
): SharedHiddenDocumentMetadata {
  if (!metadata) {
    return {};
  }

  const resolvedInlineFragmentRefs = isDocumentNode(metadata)
    ? getInlineFragmentRefs(metadata)
    : metadata.inlineFragmentRefs ??
      (metadata.source ? getInlineFragmentRefs(metadata.source) : {});
  const resolvedFragmentOwnerDocs = isDocumentNode(metadata)
    ? getFragmentOwnerDocs(metadata)
    : metadata.fragmentOwnerDocs ??
      (metadata.source ? getFragmentOwnerDocs(metadata.source) : {});

  // Query documents built from a subset of top-level fields should only keep
  // inline-fragment refs that those fields still reference.
  const inlineFragmentRefs = filterInlineFragmentRefsBySelections(
    resolvedInlineFragmentRefs,
    queryFields,
  );

  return {
    inlineFragmentRefs,
    fragmentOwnerDocs: resolvedFragmentOwnerDocs,
  };
}

export function createQueryDocument(
  queryName: string,
  queryVariablesDefinitions: ReadonlyArray<VariableDefinitionNode>,
  queryFields: ReadonlyArray<SelectionNode>,
  fragmentDefinitions: ReadonlyArray<FragmentDefinitionNode>,
  metadata?: DocumentNode | CreateQueryDocumentMetadata,
): DocumentNode {
  const queryOperationNode = createQueryOperationNode(
    queryName,
    queryVariablesDefinitions,
    queryFields,
  );

  const document = {
    kind: Kind.DOCUMENT,
    definitions: [queryOperationNode, ...fragmentDefinitions],
  };

  return attachHiddenDocumentMetadata(
    document,
    getResolvedHiddenDocumentMetadata(queryFields, metadata),
  );
}
